/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import cloneDeep from 'lodash.clonedeep';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {getMaxValueInArray} from '@ion/core/src/utils/ArrayUtil';
import {capitalizeFirstLetter} from '@ion/core/src/utils/StringUtil';
import {isValidEmail} from '@ion/core/src/utils/Validators';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {validateUsers} from '@ion/dls/src/editors/GroupEditorUtils';
import {dlsTypography} from '@ion/dls/src/theme/theme';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {UserLevel} from './UserLevel';

// available field names for the user
export enum UserInfoFields {
  FirstName = 'firstName',
  LastName = 'lastName',
  EmailAddress = 'email',
  PhoneNumber = 'phoneNumber',
};

export interface Entry {
  id: number;
  user: GenericObject;
};

export interface UsersProps {
  onChange: (user: GenericObject) => void;
  userInfo: Entry[];
  minUser?: number;
  bottomBorder?: boolean;
  required?: boolean;
};

const useStyles = makeStyles(theme => ({
  container : {
    margin : '0px'
  },
  label : {
    ...dlsTypography('body2'),
    fontSize : '16px'
  },
  subHeader : {
    display : 'flex',
    width   : '100%',
    padding : '10px 16px'
  },
  typoStyle : {
    fontSize : '14px !important',
    width    : '100%'
  },
  deleteStyle : {
    top : '25px'
  },
  iconStyle : {
    margin   : '-32px',
    position : 'relative',
    top      : '55px',
    left     : '25px'
  }
}));

/**
 * The User Info Editor provides the UI which allows to add new users,
 *  such as their firstname, lastname, email and phone numbers on new
 *  account creation.
 *
 *    import {Users} from '@ion/applications/src/pages/admin/accounts/Users';
 *
 *    <Users
 *       onChange={onChange} />
 *
 */
export const Users = ({
  onChange,
  userInfo,
  minUser = 0,
  bottomBorder = false
}: UsersProps) => {
  const classes                     = useStyles();
  const [usersLevel, setUsersLevel] = useState<Entry[]>(cloneDeep(userInfo) || []);

  useEffect(() => {
    if (minUser > 0 && isEmpty(userInfo)) {
      addNewEntry();
    }
  }, []);

  useEffect(() => {
    onChange?.(usersLevel);
  }, [usersLevel]);

  const onDelete = useCallback((id: string) => (e: MouseEvent<HTMLDivElement>) => {
    if (usersLevel?.length > minUser) {
      const identifier = Number(id);
      setUsersLevel(usersLevel.filter((user) => Number(user.id) !== identifier));
    }
  }, [usersLevel, minUser]);

  const addNewEntry = useCallback(() => {
    const {isValid, userList} = validateUsers(usersLevel);
    if (isValid) {
      const allIds = usersLevel?.map(({id}) => id);
      const maxId = usersLevel?.length ? getMaxValueInArray(allIds) : 0;
      const newEntry: Entry = {
        id   : maxId + 1,
        user : {
          firstName        : '',
          lastName         : '',
          email            : '',
          phoneNumber      : '',
          isFirstNameError : false,
          isLastNameError  : false,
          isEmailError     : false,
          isPhoneError     : false
        }
      };
      setUsersLevel([...usersLevel, newEntry]);
    } else {
      setUsersLevel(userList);
    }
  }, [usersLevel]);

  const updateEntry = useCallback(function(id: number | string, name: string, value: string): void {
    const identifier = Number(id);
    const errorEntity = `is${capitalizeFirstLetter(name)}Error`;
    const updatedData = usersLevel?.map((entry) => {
      if (Number(entry.id) === identifier) {
        entry.user[name] = value;
        if (name !== UserInfoFields.PhoneNumber) {
          if (name === UserInfoFields.EmailAddress && value !== '') {
            entry.user[errorEntity] = !isValidEmail(value);
          } else {
            entry.user[errorEntity] = !value;
          }
        }
      }
      return entry;
    });
    setUsersLevel(updatedData);
  }, [usersLevel]);

  const handleFieldChange = useCallback(function(e: ChangeEvent<HTMLInputElement>) {
    e.persist?.();
    const {name, value, id} = e.target;
    updateEntry(id, name, value);
  }, [usersLevel]);

  const onPhoneNumberChange = useCallback(function(newPhoneNumber: string, id: string) {
    updateEntry(id, UserInfoFields.PhoneNumber, newPhoneNumber);
  }, [usersLevel]);

  return (
    <div className={classes.container}>
      <FormSection bottomBorder={bottomBorder}
        title={translate('admin-groups:dialog.userInfo')}
        legend={(
          <TertiaryButton disabled={usersLevel?.length > 10} onClick={addNewEntry}>
            {translate('common:add')}
          </TertiaryButton>
        )}>
        <div className={classes.subHeader}>
          <BodyTypography className={classes.label}>
            {translate('admin-groups:dialog.fields.userCreation')}
          </BodyTypography>
        </div>
        {usersLevel?.map((item, index) => {
          return (
            <UserLevel item={item}
              index={index}
              usersLevel={usersLevel}
              minUser={minUser}
              handleFieldChange={handleFieldChange}
              onPhoneNumberChange={onPhoneNumberChange}
              onDelete={onDelete}
              classes={classes} />
          );
        })}
      </FormSection>
    </div>
  );
};

export default Users;
